/**
 * EP-160 section 2.6 and S8: "ติดตรงไหน?", the question asked when an owner or
 * admin stops onboarding, and the wizard's onboarding_abandoned event.
 *
 * Two triggers, each asked once (the server stamps the trigger when it gets
 * an answer or a skip):
 *   leave   the wizard was left before step 8, by a pagehide (reload, closing
 *           the tab, going to another site) or by in-app navigation out of
 *           /onboarding*. The wizard records it in sessionStorage; the
 *           dashboard asks when the session payload still says the leave
 *           trigger is unasked.
 *   return  the session payload says "return" (the server's 3-stalled-days
 *           rule); the dashboard asks after sign-in.
 */
import { useEffect, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { trackOnboardingAbandoned } from '@/lib/analytics'
import type { User } from '@/types'

export type BlockerTrigger = 'leave' | 'return'

export const BLOCKER_CODES = ['no_time', 'too_hard', 'other_system', 'waiting_line', 'price', 'other'] as const
export type BlockerCode = (typeof BLOCKER_CODES)[number]

export const BLOCKER_NOTE_LIMIT = 500

// The wizard's last step; leaving on it is not abandoning.
const FINAL_STEP = 8

export const BLOCKER_PENDING_KEY = 'flowrent_blocker_pending'

// Shown-this-tab markers, per account, so a question already answered is not
// raised again before the session payload is fetched afresh.
function askedKey(accountId: number, trigger: BlockerTrigger): string {
  return `flowrent_blocker_asked:${accountId}:${trigger}`
}

function readStorage(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

// The dashboard reads the markers through useSyncExternalStore, so a write
// (the wizard's unmount, an answer) re-renders it.
const listeners = new Set<() => void>()

export function subscribeBlocker(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key)
    else sessionStorage.setItem(key, value)
  } catch {
    // Storage unavailable (private mode quota): the question is simply not raised.
  }
  listeners.forEach((listener) => listener())
}

export function markLeavePending(): void {
  writeStorage(BLOCKER_PENDING_KEY, 'leave')
}

export function markBlockerAsked(accountId: number, trigger: BlockerTrigger): void {
  writeStorage(askedKey(accountId, trigger), '1')
  if (trigger === 'leave') writeStorage(BLOCKER_PENDING_KEY, null)
}

/** Which trigger to ask now, or null. Owner and admin only. */
export function blockerTriggerFor(user: User | null): BlockerTrigger | null {
  if (!user || (user.role !== 'owner' && user.role !== 'admin')) return null
  const ask = user.account.onboarding_blocker_ask
  const accountId = user.account.id
  if (ask === 'return' && readStorage(askedKey(accountId, 'return')) === null) return 'return'
  if (
    ask === 'leave' &&
    readStorage(BLOCKER_PENDING_KEY) === 'leave' &&
    readStorage(askedKey(accountId, 'leave')) === null
  ) {
    return 'leave'
  }
  return null
}

export interface BlockerAnswer {
  code: BlockerCode | 'skipped'
  trigger: BlockerTrigger
  note?: string
}

/** POST /api/onboarding/blocker. The server sends PostHog the chip and trigger, never the note. */
export function useAnswerBlocker() {
  return useMutation({
    mutationFn: async (answer: BlockerAnswer) => {
      const response = await api.post('/api/onboarding/blocker', answer)
      return response.data
    },
  })
}

/**
 * Mounted by the wizard while a step is showing. On pagehide before step 8 it
 * records the leave trigger and sends onboarding_abandoned { step,
 * seconds_on_step }; when the wizard unmounts because the route left
 * /onboarding*, it records the leave trigger too.
 */
export function useWizardAbandonment(step: number, active: boolean): void {
  const stepRef = useRef(step)
  // Set by the effect below on mount and on every step change.
  const stepStartedAt = useRef(0)

  useEffect(() => {
    stepRef.current = step
    stepStartedAt.current = Date.now()
  }, [step])

  useEffect(() => {
    if (!active) return
    const handlePageHide = () => {
      if (stepRef.current >= FINAL_STEP) return
      markLeavePending()
      trackOnboardingAbandoned({
        step: stepRef.current,
        seconds_on_step: Math.round((Date.now() - stepStartedAt.current) / 1000),
      })
    }
    window.addEventListener('pagehide', handlePageHide)
    return () => {
      window.removeEventListener('pagehide', handlePageHide)
      // By the time this cleanup runs the router has already moved the URL,
      // so a path outside /onboarding means the wizard was left in the app.
      if (stepRef.current < FINAL_STEP && !window.location.pathname.startsWith('/onboarding')) {
        markLeavePending()
      }
    }
  }, [active])
}
